/**
 * Note placement (W2-03 note-lane; DESIGN.md §5.3 "Placing", §8.2-8.3; RPE NOTE-01..04, NOTE-14).
 *
 *  - Tools 1-4 (Tap, Drag, Flick, Hold) place with the mouse: a ghost follows the pointer, snapped to the beat grid
 *    and (when on) the X grid; the hint bar says what a click does ("Click: place a Tap at 12:3/4, X -135"). A click
 *    on an existing note selects it instead of stacking; Shift+click stacks on purpose. The Hold tool places by
 *    press, drag up, release (minimum one snap step; Esc cancels).
 *  - Q / W / E place a Tap / Drag / Flick at the mouse whatever the tool; R starts a Hold at the mouse, R again (or a
 *    click) ends it, the wheel keeps scrolling in between and Esc cancels. Shift+Q / W / E place at the playhead beat
 *    (X from the mouse over the lane, else the last X); Shift+R ... Shift+R plays a Hold in while the audio runs.
 *  - Alt bypasses snapping (times still rounded to 1/1920 beat; in the Alt-free variant Ctrl+Shift does it).
 *  - New notes use the "New note defaults" (settings.editing.noteDefaults). UI-mover lines refuse placement (the lane
 *    says why) and locked lines refuse edits.
 * Placed notes are selected (the core command sets the selection) and the transaction focuses their beat.
 */

import { Beats } from '../../core/time/beat';
import type { Beat } from '../../core/time/types';
import type { MessageRef } from '../../core/util/types';
import type { LineId, NoteId, NoteKind, NoteTemplate } from '../../core/model/types';
import { Core } from '../../core/commands/index';
import { Defaults } from '../../core/model/defaults';
import type { AppServices } from '../../app/registry/types';
import type { PlacementState, SnapState } from '../../state/types';
import type { PlacementGhost } from '../../render/timeline/laneDraw';
import { m } from './noteLane.messages';
import {
  activeLineOf,
  hostOf,
  beatText,
  isLockedLine,
  isUiMoverLine,
  kindLabel,
  laneSession,
  livePointer,
  playheadBeat,
  ref,
  beatCountText,
  secCountText,
  snapBeatAt,
  snapStepBeat,
  snapXAt,
  tempoOf,
  type LanePointerInfo,
  gestureHints,
} from './laneSession';

/** Kind placed by a tool ('select' and 'eraser' place nothing). */
export function toolKind(tool: string): NoteKind | null {
  return tool === 'tap' || tool === 'drag' || tool === 'flick' || tool === 'hold' ? tool : null;
}

/** "New note defaults" (falls back to the model defaults when settings are unavailable). */
export function noteTemplate(services: Pick<AppServices, 'settings'>): NoteTemplate {
  try {
    return services.settings.value.editing.noteDefaults;
  } catch {
    return Defaults.noteTemplate();
  }
}

/** Whether notes may be placed on a line now: true, or the reason. */
export function placeAllowed(services: AppServices, line: LineId | null): true | MessageRef {
  const store = services.store;
  if (store === null) return ref(m.reasonNoProject);
  if (line === null || !store.doc.lines.has(line)) return ref(m.reasonNoLine);
  if (isUiMoverLine(store, line)) return ref(m.reasonUiMover);
  if (isLockedLine(services, line)) return ref(m.reasonLocked);
  if (store.gestureActive) return ref(m.reasonGesture);
  return true;
}

/** Pointer over a lane showing a line, or null. */
export function lanePointer(services: AppServices): LanePointerInfo | null {
  const p = livePointer(laneSession(services));
  return p !== null && p.line !== null ? p : null;
}

/**
 * Beat (f64) under the mouse anywhere in the timeline: the lane pointer, else the host's hover over any column (time
 * is shared by every column), recomputed for the current view; null when the mouse is outside the timeline.
 */
export function mouseBeatF64(services: AppServices): { readonly beat: number; readonly alt: boolean } | null {
  const s = laneSession(services);
  const p = livePointer(s);
  if (p !== null) return { beat: p.beat, alt: p.alt };
  const host = hostOf(s);
  try {
    const h = host?.hoverInfo() ?? null;
    return h === null || host === null ? null : { beat: host.axis.beatAtY(h.y), alt: false };
  } catch {
    return null;
  }
}

export interface PlaceArgs {
  readonly line: LineId;
  readonly kind: NoteKind;
  readonly t: Beat;
  readonly x: number;
  /** Holds only. */
  readonly end?: Beat;
  readonly command: string;
}

/** Places one note (one undoable step); toasts the reason when the core refuses. Returns its id. */
export function placeNote(services: AppServices, a: PlaceArgs): NoteId | null {
  const store = services.store;
  if (store === null) return null;
  const allowed = placeAllowed(services, a.line);
  if (allowed !== true) {
    services.ui.toast(allowed, { kind: 'warning' });
    return null;
  }
  const template = noteTemplate(services);
  const draft =
    a.kind === 'hold' && a.end !== undefined
      ? { line: a.line, kind: a.kind, t: a.t, end: a.end, x: a.x }
      : { line: a.line, kind: a.kind, t: a.t, x: a.x };
  const out = store.transact(
    { label: ref(m.txPlace, { kind: a.kind }), command: a.command, focus: { beat: a.t, line: a.line } },
    (tx) => Core.notes.addNote(tx, { draft, template }),
  );
  if (!out.ok) {
    services.ui.toast(out.reason, { kind: 'error' });
    return null;
  }
  for (const n of out.notes) services.ui.toast(n, { kind: 'info' });
  return out.value;
}

/** Hold end for a placement: at least one snap step after the start; a backwards drag swaps the ends. */
export function holdSpan(start: Beat, end: Beat, snap: SnapState): { readonly t: Beat; readonly end: Beat } {
  const c = Beats.cmp(start, end);
  if (c === 0) return { t: start, end: Beats.add(start, snapStepBeat(snap)) };
  return c < 0 ? { t: start, end } : { t: end, end: start };
}

/** Places a Tap / Drag / Flick at the mouse (Q / W / E). */
export function placeAtMouse(services: AppServices, kind: Exclude<NoteKind, 'hold'>, command: string): NoteId | null {
  const p = lanePointer(services);
  if (p?.line == null) {
    services.ui.toast(ref(m.reasonNotOverLane), { kind: 'info' });
    return null;
  }
  const snap = services.editor.getState().snap;
  const free = p.alt;
  return placeNote(services, {
    line: p.line,
    kind,
    t: snapBeatAt(p.beat, snap, free),
    x: snapXAt(p.x, snap, free),
    command,
  });
}

/** Places a Tap / Drag / Flick at the playhead beat (Shift+Q / W / E). */
export function placeAtPlayhead(
  services: AppServices,
  kind: Exclude<NoteKind, 'hold'>,
  command: string,
): NoteId | null {
  const line = activeLineOf(services);
  if (line === null) {
    services.ui.toast(ref(m.reasonNoLine), { kind: 'info' });
    return null;
  }
  const s = laneSession(services);
  const snap = services.editor.getState().snap;
  const x = s.pointer?.line === line ? s.pointer.x : s.lastX;
  return placeNote(services, {
    line,
    kind,
    t: snapBeatAt(playheadBeat(services), snap, false),
    x: snapXAt(x, snap, false),
    command,
  });
}

// ---------------------------------------------------------------------------------------------------------------
// R ... R holds
// ---------------------------------------------------------------------------------------------------------------

/** Gesture hints while a Hold is being placed with R (or Shift+R). */
export function holdHints(services: AppServices, atPlayhead: boolean): void {
  gestureHints(services, [
    { chord: atPlayhead ? 'Shift+KeyR' : 'KeyR', label: ref(m.gestureEndHold) },
    { chord: 'Escape', label: ref(m.gestureCancel) },
    { chord: 'Alt', label: ref(m.gestureNoSnap) },
  ]);
}

/**
 * R (atPlayhead false) / Shift+R (true): starts a Hold, or ends the one being placed. The start is the snapped mouse
 * beat (or playhead); the end likewise when R comes again. Returns the placed note when the Hold was finished.
 */
export function toggleHoldPlacement(services: AppServices, atPlayhead: boolean, command: string): NoteId | null {
  const e = services.editor.getState();
  const placing = e.placement;
  if (placing?.kind === 'hold') return finishHold(services, placing, atPlayhead ? 'playhead' : 'mouse', command);
  const snap = e.snap;
  const s = laneSession(services);
  let line: LineId | null;
  let beat: number;
  let x: number;
  let free = false;
  if (atPlayhead) {
    line = activeLineOf(services);
    beat = playheadBeat(services);
    x = s.pointer?.line === line && line !== null ? s.pointer.x : s.lastX;
  } else {
    const p = lanePointer(services);
    if (p === null) {
      services.ui.toast(ref(m.reasonNotOverLane), { kind: 'info' });
      return null;
    }
    line = p.line;
    beat = p.beat;
    x = p.x;
    free = p.alt;
  }
  const allowed = placeAllowed(services, line);
  if (allowed !== true || line === null) {
    services.ui.toast(allowed === true ? ref(m.reasonNoLine) : allowed, { kind: 'warning' });
    return null;
  }
  e.setPlacement({ kind: 'hold', line, start: snapBeatAt(beat, snap, free), x: snapXAt(x, snap, free), atPlayhead });
  holdHints(services, atPlayhead);
  s.redraw(['overlay']);
  return null;
}

/** Ends a Hold placement at the mouse / playhead / an explicit beat. */
export function finishHold(
  services: AppServices,
  placing: Extract<PlacementState, { kind: 'hold' }>,
  at: 'mouse' | 'playhead' | { readonly beat: number; readonly free: boolean },
  command: string,
): NoteId | null {
  const e = services.editor.getState();
  const snap = e.snap;
  let endBeat: Beat;
  if (typeof at === 'object') endBeat = snapBeatAt(at.beat, snap, at.free);
  else if (at === 'playhead' || placing.atPlayhead) endBeat = snapBeatAt(playheadBeat(services), snap, false);
  else {
    const p = mouseBeatF64(services);
    endBeat = p === null ? snapBeatAt(playheadBeat(services), snap, false) : snapBeatAt(p.beat, snap, p.alt);
  }
  const span = holdSpan(placing.start, endBeat, snap);
  e.setPlacement(null);
  gestureHints(services, null);
  laneSession(services).redraw(['overlay']);
  return placeNote(services, { line: placing.line, kind: 'hold', t: span.t, end: span.end, x: placing.x, command });
}

/** Esc while a Hold is being placed. */
export function cancelHoldPlacement(services: AppServices): void {
  const e = services.editor.getState();
  if (e.placement?.kind !== 'hold') return;
  e.setPlacement(null);
  gestureHints(services, null);
  laneSession(services).redraw(['overlay']);
}

// ---------------------------------------------------------------------------------------------------------------
// Ghosts and hints
// ---------------------------------------------------------------------------------------------------------------

/** Translated length of a hold ("2 beats, 1.000 s", "1/2 beat, 0.250 s", "1 1/4 beats, 0.625 s"). */
export function holdLengthLabel(services: AppServices, t: Beat, end: Beat): string {
  const tempo = tempoOf(services);
  const sec = tempo === null ? 0 : tempo.secAt(end) - tempo.secAt(t);
  const len = beatCountText(Beats.sub(end, t));
  return services.i18n.t(m.holdLength, { beats: len.text, one: len.one, sec: secCountText(sec) });
}

/**
 * The placement ghost of a lane for the current pointer: the Hold being placed with R (from its start to the
 * pointer / playhead), the Hold being drawn with the Hold tool, or the note a click would place with a note tool.
 */
export function placementGhost(services: AppServices, line: LineId | null): PlacementGhost | null {
  const store = services.store;
  if (store === null || line === null) return null;
  const e = services.editor.getState();
  const s = laneSession(services);
  const tpl = noteTemplate(services);
  const refused = placeAllowed(services, line) !== true;
  const base = { size: tpl.size, fake: tpl.fake, below: !tpl.above, refused };
  const placing = e.placement;
  if (placing?.kind === 'hold' && placing.line === line) {
    const p = placing.atPlayhead ? null : mouseBeatF64(services);
    const endBeat = p === null ? snapBeatAt(playheadBeat(services), e.snap, false) : snapBeatAt(p.beat, e.snap, p.alt);
    const span = holdSpan(placing.start, endBeat, e.snap);
    return {
      ...base,
      kind: 'hold',
      beat: Beats.toF64(span.t),
      end: Beats.toF64(span.end),
      x: placing.x,
      label: holdLengthLabel(services, span.t, span.end),
    };
  }
  const draw = s.gesture.holdDraw;
  if (draw !== null && draw.line === line) {
    const t = Beats.fromF64(Math.min(draw.start, draw.end), 1920);
    const end = Beats.fromF64(Math.max(draw.start, draw.end), 1920);
    const span = holdSpan(t, end, e.snap);
    return {
      ...base,
      kind: 'hold',
      beat: Beats.toF64(span.t),
      end: Beats.toF64(span.end),
      x: draw.x,
      label: holdLengthLabel(services, span.t, span.end),
    };
  }
  const kind = toolKind(e.tool);
  const p = livePointer(s);
  if (kind === null || p?.line !== line || placing !== null || s.gesture.move !== null) return null;
  const t = snapBeatAt(p.beat, e.snap, p.alt);
  const x = snapXAt(p.x, e.snap, p.alt);
  const tb = Beats.toF64(t);
  return {
    ...base,
    kind,
    beat: tb,
    end: kind === 'hold' ? Beats.toF64(Beats.add(t, snapStepBeat(e.snap))) : tb,
    x,
    label: null,
  };
}

/** Hint-bar sentence for a hover with a note tool (or the eraser); null clears it. */
export function updateToolHints(services: AppServices, p: LanePointerInfo | null, overNote: boolean): void {
  const e = services.editor.getState();
  if (e.placement !== null) return;
  const s = laneSession(services);
  const set = (key: string, items: Parameters<AppServices['ui']['setGestureHints']>[0]): void => {
    // The hint bar re-renders on every change: only send a new sentence when it differs.
    if (s.hintKey === key) return;
    s.hintKey = key;
    services.ui.setGestureHints(items);
  };
  if (p?.line == null) {
    set('', null);
    return;
  }
  const click = services.i18n.t(m.mouseClick);
  if (e.tool === 'eraser') {
    if (isLockedLine(services, p.line)) set('refused:locked', [{ chord: click, label: ref(m.reasonLocked) }]);
    else set('eraser', [{ chord: click, label: ref(m.hintErase) }]);
    return;
  }
  const kind = toolKind(e.tool);
  if (kind === null || overNote) {
    set('', null);
    return;
  }
  const allowed = placeAllowed(services, p.line);
  if (allowed !== true) {
    set(`refused:${allowed.key}`, [{ chord: click, label: allowed }]);
    return;
  }
  const t = snapBeatAt(p.beat, e.snap, p.alt);
  const x = snapXAt(p.x, e.snap, p.alt);
  const params = { kind: kindLabel(services, kind), beat: beatText(services, t), x: String(x) };
  set(`place:${kind}:${params.beat}:${params.x}`, [
    { chord: click, label: ref(kind === 'hold' ? m.hintPlaceHold : m.hintPlace, params) },
    { chord: 'Alt', label: ref(m.gestureNoSnap) },
    { chord: `Shift+${click}`, label: ref(m.gestureStack) },
  ]);
}

/** Forget the last tool hint (a gesture or a placement replaced the hint bar's gesture hints). */
export function resetToolHints(services: AppServices): void {
  laneSession(services).hintKey = null;
}
